import React, { ReactNode, forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { style } from 'typestyle';
import { Loader } from './Loader';
import { PerfectScrollbar } from './PerfectScrollbar';

const DEFAULT_TRESHOLD = 500;

export interface Props {
  /**
     * Define how to render container
     * It will override the default PerfectScrollBar behavior
     */
  children?: ReactNode
  container?: (
    InfiniteScrollerProps: {
      onScroll: (e: React.UIEvent<HTMLDivElement>) => any
    },
    children: ReactNode[]
  ) => ReactNode
  threshold?: number
  load: (page: number) => Promise<void>
  loader?: boolean | ReactNode
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => any // Get the original event
  noMore: boolean
  containerRef?: React.RefObject<HTMLElement> // To override default ref
  horizontal?: boolean
  initialPage?: number
}

export interface InfiniteScrollerHandle {
  resetPage: () => void
}

type ScrollProps = {
  clientProp: 'clientWidth'
  scrollProp: 'scrollLeft'
} | {
  clientProp: 'clientHeight'
  scrollProp: 'scrollTop'
};

/**
 * Encapsulate a scroller or any other component
 * and check to load more content
 */
const InfiniteScroller = forwardRef<InfiniteScrollerHandle, Props>(function InfiniteScroller(
  { children, container, threshold, load, loader, onScroll, noMore, containerRef, horizontal, initialPage },
  ref,
) {
  const getInitialPage = () => (typeof initialPage === 'number' ? initialPage : 1);
  const getThreshold = () => (typeof threshold === 'number' ? threshold : DEFAULT_TRESHOLD);

  const [displayLoader, setDisplayLoader] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const loading = useRef(false);
  const page = useRef(getInitialPage());
  const loaderRef = useRef<HTMLDivElement>(null);

  const scrollProps: ScrollProps = horizontal
    ? { clientProp: 'clientWidth', scrollProp: 'scrollLeft' }
    : { clientProp: 'clientHeight', scrollProp: 'scrollTop' };

  useImperativeHandle(ref, () => ({
    resetPage() {
      page.current = getInitialPage();
    },
  }));

  function getLoaderComponent() {
    if (loader === false) {
      return null;
    }
    if (React.isValidElement(loader)) {
      return loader;
    }
    return (
      <div className={loaderContainerClass} ref={loaderRef} key="infinite-loader">
        <Loader />
      </div>
    );
  }

  function listener(targetElement: HTMLElement | null) {
    const containerNode = wrapperRef.current?.firstChild as HTMLElement | null;
    if (loading.current || noMore || !targetElement || !containerNode) {
      return;
    }

    // Use given containerRef or use infinite scroller's children
    const firstChild = containerRef?.current || containerNode.firstChild;

    if (firstChild?.nodeType !== Node.ELEMENT_NODE || loaderRef.current === firstChild) {
      return;
    }
    const child = firstChild as HTMLElement;
    const { clientProp, scrollProp } = scrollProps;

    const offset = (child[clientProp] || 0) - containerNode[clientProp] - containerNode[scrollProp];
    if (offset <= getThreshold()) {
      loading.current = true;
      setDisplayLoader(true);
      load(page.current).then(() => {
        page.current++;
      }).catch((error) => {
        console.error(error);
      }).finally(() => {
        loading.current = false;
        setDisplayLoader(false);
      });
    }
  }

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    listener(e.nativeEvent.target as any);
    onScroll?.(e);
  }

  const defaultContainer: Props['container'] = (props, c) => (
    <PerfectScrollbar {...props}>
      {c}
    </PerfectScrollbar>
  );

  const renderContainer = container ?? defaultContainer!;
  const scrollerProps = { onScroll: handleScroll };
  const childrens = [children];
  if (displayLoader) {
    childrens.push(getLoaderComponent());
  }

  return (
    <div ref={wrapperRef} style={{ display: 'contents' }}>
      {renderContainer(scrollerProps, childrens)}
    </div>
  );
});

const loaderContainerClass = style({
  textAlign: 'center',
  padding: 30,
});

export { InfiniteScroller };
